/**
 * Обновление страницы после выката новой версии.
 *
 * Сборка кладёт файлы с новыми именами (index-B-o2hjrq.js), а вкладка,
 * открытая до выката, помнит старые. Когда она пытается подгрузить кусок
 * приложения — раздел протокола, лендинг, панель судьи — файла уже нет, и
 * пользователь видит страницу с ошибкой, хотя ничего не сломалось.
 *
 * Такую ошибку узнаём по тексту и перезагружаем страницу сами — один раз.
 * Повтор защищён отметкой в sessionStorage: если после перезагрузки ошибка
 * вернулась, значит дело не в выкате, и человеку показываем обычный экран.
 */
const RELOAD_MARK = "judix:chunk-reload-at";
const RELOAD_GUARD_MS = 20000;

const CHUNK_ERROR_SIGNS = [
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
  "loading chunk",
  "loading css chunk",
  "chunkloaderror",
  "unable to preload css",
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: string }).name || "";
  const message = (error as { message?: string }).message || String(error);
  const text = `${name} ${message}`.toLowerCase();
  return CHUNK_ERROR_SIGNS.some((sign) => text.includes(sign));
}

/** Пробуем перезагрузиться. false — уже пробовали только что, не зацикливаемся. */
export function reloadOnceForNewVersion(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_MARK) || 0);
    if (last && Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_MARK, String(Date.now()));
  } catch {
    // Приватный режим без sessionStorage — лучше перезагрузиться один раз,
    // чем показать ошибку; цикл здесь маловероятен.
  }
  window.location.reload();
  return true;
}

/** Vite сам сообщает о неудачной подгрузке — ловим и обновляемся. */
export function installChunkReloadHandler(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    reloadOnceForNewVersion();
  });
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      reloadOnceForNewVersion();
    }
  });
}
